import type { BrandDispute } from '../api/payment/brand-deal.api';
import { caForVnd, formatCa } from './individual-order';
import { parseServerDate } from './server-date';

// Hàm thuần cho "Kháng cáo bản quyền" (M4) — kiểm form kháng cáo creator/giải trình doanh nghiệp, đếm
// ngày còn hạn kháng cáo, nhãn trạng thái kháng cáo. Tách khỏi component để test không cần dựng React.
// ĐÚNG câu chữ + thứ tự với `BrandDisputeRules` phía payment (xem BrandDisputeHandlers.cs).

export const BRAND_DISPUTE_LIMITS = {
  descriptionMinLength: 30,
  descriptionMaxLength: 2000,
  maxLinks: 5,
  linkMaxLength: 500,
  maxImages: 5,
  responseMaxLength: 2000,
} as const;

/** URL tuyệt đối http/https thật sự (có host) — khác `isValidHttpsUrl` (brand-deal.ts, chỉ nhận https,
 * dùng cho link đích chiến dịch); bằng chứng kháng cáo nhận cả http lẫn https theo `BrandDisputeRules.IsHttpUrl`. */
const isValidHttpUrl = (value: string): boolean => {
  const v = value?.trim() ?? '';
  if (!v) return false;
  try {
    const u = new URL(v);
    return (u.protocol === 'http:' || u.protocol === 'https:') && u.hostname.length > 0;
  } catch {
    return false;
  }
};

const normalizeLinks = (links: string[] | null | undefined): string[] =>
  (links ?? []).map((l) => l.trim()).filter((l) => l.length > 0);

/** null = hợp lệ, ngược lại câu lỗi — khớp `BrandDisputeRules.ValidateLinks` (đếm trước, hợp lệ từng
 * link sau — length lẫn scheme sai đều chung 1 câu "Link không hợp lệ."). */
const validateLinks = (links: string[] | null | undefined): string | null => {
  const { maxLinks, linkMaxLength } = BRAND_DISPUTE_LIMITS;
  const list = normalizeLinks(links);
  if (list.length > maxLinks) return `Tối đa ${maxLinks} link.`;
  if (list.some((l) => l.length > linkMaxLength || !isValidHttpUrl(l))) return 'Link không hợp lệ.';
  return null;
};

export interface DisputeEvidenceImage {
  id: string;
  url: string;
}

export interface DisputeFormInput {
  description: string;
  links: string[];
  images?: DisputeEvidenceImage[];
}

/**
 * Kiểm form kháng cáo creator — trả `null` khi hợp lệ, ngược lại câu lỗi tiếng Việt đầu tiên vi phạm,
 * ĐÚNG thứ tự + ĐÚNG câu chữ với `BrandDisputeRules.ValidateOpen` phía payment: mô tả (30–2.000 ký tự)
 * ⇒ link bằng chứng (tối đa 5, http/https, mỗi link ≤500 ký tự) ⇒ ảnh bằng chứng (tối đa 5, phải có id +
 * url hợp lệ — ảnh thiếu/sai dùng CHUNG câu lỗi với link sai).
 */
export const validateDisputeForm = (input: DisputeFormInput): string | null => {
  const { descriptionMinLength, descriptionMaxLength, maxImages } = BRAND_DISPUTE_LIMITS;
  const text = input.description?.trim() ?? '';
  if (text.length < descriptionMinLength || text.length > descriptionMaxLength)
    return `Mô tả kháng cáo từ ${descriptionMinLength} đến ${descriptionMaxLength.toLocaleString('vi-VN')} ký tự.`;

  const linkError = validateLinks(input.links);
  if (linkError) return linkError;

  const images = input.images ?? [];
  if (images.length > maxImages) return `Tối đa ${maxImages} ảnh.`;
  if (images.some((img) => !img || !img.id?.trim() || !isValidHttpUrl(img.url))) return 'Link không hợp lệ.';

  return null;
};

export interface ResponseFormInput {
  response: string;
  links: string[];
}

/** Chỉ có ở FE — không có ở `BrandDisputeRules.ValidateResponse` phía payment: doanh nghiệp chỉ được
 * giải trình đúng 1 lần nên chặn gửi trắng (không chữ, không link) ngay từ form, tránh gửi "rỗng" mất
 * lượt oan. */
export const RESPONSE_REQUIRED_MESSAGE = 'Bạn cần giải trình hoặc đính kèm ít nhất 1 link.';

/**
 * Kiểm form giải trình doanh nghiệp — trả `null` khi hợp lệ, ngược lại câu lỗi tiếng Việt đầu tiên vi
 * phạm: bắt buộc có chữ hoặc link (chốt riêng của FE) ⇒ giải trình tối đa 2.000 ký tự ⇒ link (tối đa 5,
 * http/https, mỗi link ≤500 ký tự) — ĐÚNG câu chữ với `BrandDisputeRules.ValidateResponse` từ bước 2 trở đi.
 */
export const validateResponseForm = (input: ResponseFormInput): string | null => {
  const { responseMaxLength } = BRAND_DISPUTE_LIMITS;
  const text = input.response?.trim() ?? '';
  const links = normalizeLinks(input.links);
  if (!text && links.length === 0) return RESPONSE_REQUIRED_MESSAGE;
  if (text.length > responseMaxLength) return `Giải trình tối đa ${responseMaxLength.toLocaleString('vi-VN')} ký tự.`;

  const linkError = validateLinks(input.links);
  if (linkError) return linkError;

  return null;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

/**
 * Đếm thời gian còn lại tới `endsAtIso` (hạn kháng cáo, 14 ngày kể từ lúc từ chối) — từ 1 ngày trở lên
 * hiện ngày (làm tròn lên), dưới 1 ngày hiện giờ (làm tròn lên), hết hạn/không có hạn hiện câu lỗi ĐÚNG
 * với `BrandDealConst.DisputeWindowExpiredMessage` phía payment. Dùng cho nhãn "Còn X ngày để kháng cáo"
 * ở bàn soạn bài + danh sách nhiệm vụ (creator).
 */
export const daysLeftLabel = (endsAtIso: string | null | undefined, nowMs: number): string => {
  if (!endsAtIso) return 'Đã hết hạn kháng cáo.';
  const due = parseServerDate(endsAtIso)?.getTime() ?? NaN;
  if (!Number.isFinite(due)) return 'Đã hết hạn kháng cáo.';
  const diffMs = due - nowMs;
  if (diffMs <= 0) return 'Đã hết hạn kháng cáo.';
  if (diffMs < DAY_MS) return `Còn ${Math.ceil(diffMs / HOUR_MS)} giờ để kháng cáo`;
  return `Còn ${Math.ceil(diffMs / DAY_MS)} ngày để kháng cáo`;
};

/** Lý do quyết kháng cáo (admin) VÀ lý do khoá thủ công tài khoản doanh nghiệp (SuperAdmin) — CÙNG
 * giới hạn + CÙNG câu lỗi phía payment (`BrandDealConst.DisputeReasonInvalidMessage`, dùng chung cho cả
 * `DecideDisputeHandler` lẫn `AdminBanAccountHandler`). */
export const DECISION_REASON_LIMITS = { minLength: 10, maxLength: 1000 } as const;

/** null = hợp lệ, ngược lại câu lỗi — khớp `BrandDealConst.DisputeReasonInvalidMessage`. */
export const validateDecisionReason = (reason: string | null | undefined): string | null => {
  const { minLength, maxLength } = DECISION_REASON_LIMITS;
  const text = reason?.trim() ?? '';
  if (text.length < minLength || text.length > maxLength) return 'Lý do từ 10 đến 1.000 ký tự.';
  return null;
};

/** Nhãn 3 trạng thái kháng cáo (`BrandDisputeStatus` phía payment) — mã lạ trả lại nguyên chuỗi (không
 * rõ ⇒ không tự bịa chữ). */
export const disputeStatusLabel = (status: string): string => {
  switch (status) {
    case 'Open':
      return 'Đang chờ xử lý';
    case 'Upheld':
      return 'Kháng cáo được chấp nhận';
    case 'Dismissed':
      return 'Không đủ căn cứ';
    default:
      return status;
  }
};

// ─────────── Chia tiền khi quyết kháng cáo (spec 2026-10-10-brand-order-rules §3, §4) ───────────

/** Tỉ lệ creator nhận admin chọn được — bước 10% như payment `BrandDisputeSplit.Validate`. */
export const DISPUTE_PERCENT_OPTIONS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100] as const;

export interface DisputeSplitInput {
  priceVnd: number;
  feePercent: number;
  ordererKind?: string | null;
  vndPerCa?: number | null;
  percent: number;
}

export interface DisputeSplitPreview {
  creatorNetCa: number;
  refundVnd: number;
  refundCa: number | null;
  creatorLabel: string;
  refundLabel: string;
}

/**
 * Xem trước tiền hai bên — ĐÚNG công thức `BrandDisputeSplit` phía payment: phần creator C = ⌊giá × p ÷ 100⌋, creator nhận
 * ⌊C × (100 − phí) ÷ 100⌋ Ca; doanh nghiệp được hoàn giá − C đồng; cá nhân được hoàn ⌊Ca đã giữ × (100 − p) ÷ 100⌋ Ca nạp
 * (Ca đã giữ = `caForVnd(giá, tỉ giá)` làm tròn lên như lúc giữ).
 */
export const disputeSplitPreview = (i: DisputeSplitInput): DisputeSplitPreview => {
  const share = Math.floor((i.priceVnd * i.percent) / 100);
  const creatorNetCa = Math.floor((share * (100 - i.feePercent)) / 100);
  const refundVnd = i.priceVnd - share;
  const refundCa =
    i.ordererKind === 'Individual' ? Math.floor((caForVnd(i.priceVnd, i.vndPerCa ?? 1) * (100 - i.percent)) / 100) : null;
  const creatorLabel = i.percent === 0 ? 'Creator không nhận tiền' : `Creator nhận ${formatCa(creatorNetCa)} Ca sau phí`;
  const refundLabel =
    i.percent === 100
      ? 'Người đặt không được hoàn tiền'
      : i.percent === 0
      ? 'Suất trả lại cho người đặt như khi không đủ căn cứ'
      : refundCa != null
      ? `Hoàn người đặt ${formatCa(refundCa)} Ca nạp`
      : `Hoàn người đặt ${formatCa(refundVnd)}đ`;
  return { creatorNetCa, refundVnd, refundCa, creatorLabel, refundLabel };
};

export type DisputeViewer = 'Creator' | 'Business' | 'Admin';

/** Dòng kết quả trên hộp kháng cáo (spec §4): tỉ lệ, kèm số mình nhận (creator) hoặc được hoàn (người đặt). Chưa quyết, hoặc
 * payment cũ chưa trả `creatorPercent` ⇒ không có dòng nào (hộp vẫn hiện nhãn trạng thái cũ). */
export const disputeResultLines = (
  d: Pick<BrandDispute, 'status'> & Partial<Pick<BrandDispute, 'creatorPercent' | 'creatorReceivedCa' | 'refundVnd' | 'refundCa'>>,
  viewer: DisputeViewer
): string[] => {
  if (d.status === 'Open' || d.creatorPercent == null) return [];
  const lines = [`Trọng tài quyết: creator nhận ${d.creatorPercent}%`];
  if (viewer === 'Creator') {
    if (d.creatorPercent > 0 && d.creatorReceivedCa != null) lines.push(`Bạn nhận ${formatCa(d.creatorReceivedCa)} Ca sau phí`);
    return lines;
  }
  const who = viewer === 'Business' ? 'Bạn được hoàn' : 'Người đặt được hoàn';
  if (d.refundCa != null) lines.push(`${who} ${formatCa(d.refundCa)} Ca nạp`);
  else if (d.refundVnd != null) lines.push(`${who} ${formatCa(d.refundVnd)}đ`);
  return lines;
};

export const disputeDecidedToast = (percent: number): string =>
  percent === 0
    ? 'Đã kết luận không đủ căn cứ, suất đã được trả.'
    : percent === 100
    ? 'Đã kết luận creator đúng, creator nhận đủ giá suất sau phí.'
    : `Đã chia tiền: creator nhận ${percent}%, phần còn lại hoàn cho người đặt.`;

/** Bước "Xử phạt người đặt đơn" (phần 3) hiện sau khi quyết, khi creator nhận từ 10% trở lên (spec §4). */
export const shouldOfferSanction = (percent: number): boolean => percent >= 10;
